<template>
  <v-flex class="asset-edit" xs12>
    <v-flex class="asset-edit__responsive" pa-5>
      <v-flex v-if="isEditable() && !viewFromNetwork" class="alert__box__warning">
        <i class="fas fa-exclamation-triangle"></i> {{ assetSummaryConstants.REVIEW_PENDING_WARNING }}
      </v-flex>
      <v-flex v-if="isDenied()" class="alert__box__error">
        <i class="fas fa-ban"></i>{{ assetSummaryConstants.DENIED_REASON }} <span>{{ record.deniedReason }}</span>
      </v-flex>
      <v-form ref="assetSummaryForm" v-model="valid">
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.NAME_OF_RESTAURANT }} {{ memberProfileConstants.ASTERISK }}
            </v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <googleSearch ref="googleSearch" :isSearchDisabled=isEditable() :record="record"
                          :requiredFieldError="assetSummaryConstants.ASSETNAME_REQUIRED"
                          v-on:place_changed="fillRecordData"></googleSearch>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.BRAND_OF_HOTEL }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field v-model="record.brand" :disabled="isEditable()" data-qe-id="hotelBrandTxt"></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.COUNTRY }} {{ memberProfileConstants.ASTERISK }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-autocomplete
                v-model="record.country"
                :attach="true"
                :disabled="isEditable()"
                :items="countries"
                :rules="memberProfileConstants.COUNTRY_REQUIRED"
                class="border-none"
                data-qe-id="countryDropdown"
                required
                @change="onCountryChange($event)">
            </v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.REGION }} {{ memberProfileConstants.ASTERISK }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-autocomplete
                v-model="record.region"
                :attach="true"
                :disabled="isEditable()"
                :items="Object.keys(countryAndRegions)"
                :rules="assetSummaryConstants.regionRule"
                class="border-none"
                data-qe-id="regionDropdown"
                required
                @change="onRegionsChange($event)"></v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.STREET_ADDRESS1 }} {{ memberProfileConstants.ASTERISK }}
            </v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field
                v-model="record.address1"
                :disabled="isEditable()"
                :maxLength="assetSummaryConstants.ADDRESS_LIMIT"
                :rules="assetSummaryConstants.streetAddressRule"
                data-qe-id="streetAdress1Txt"
                required
                @input="isFieldChange=true">
            </v-text-field>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.STREET_ADDRESS2 }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field
                v-model="record.address2"
                :disabled="isEditable()"
                :maxLength="assetSummaryConstants.ADDRESS_LIMIT"
                data-qe-id="streetAdress2Txt">
            </v-text-field>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.CITY }} {{ memberProfileConstants.ASTERISK }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field
                v-model="record.city"
                :disabled="isEditable()"
                :maxLength="assetSummaryConstants.CITY_LIMIT"
                :rules="assetSummaryConstants.cityRule"
                data-qe-id="cityTxt"
                required
                @input="isFieldChange=true">
            </v-text-field>
          </v-flex>
        </v-layout>
        <v-layout v-if="states.length > 0" class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.STATE }} {{ memberProfileConstants.ASTERISK }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-autocomplete
                v-model="record.state"
                :attach="true"
                :disabled="isEditable()"
                :items="states"
                :rules="assetSummaryConstants.stateRule"
                class="border-none"
                data-qe-id="stateDropDown"
                item-text="stateProvince"
                required
                @change="isFieldChange=true">
            </v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.ZIP_CODE }} {{ memberProfileConstants.ASTERISK }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field
                v-model="record.zip"
                :disabled="isEditable()"
                :maxLength="assetSummaryConstants.ZIP_LIMIT"
                :rules="assetSummaryConstants.zipRule"
                data-qe-id="zipcodeTxt"
                required
                @input="isFieldChange=true"
                @keypress="zipPostalCodeValidation">
            </v-text-field>
          </v-flex>
        </v-layout>
        <v-layout v-if="!viewFromNetwork" class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.RELATIONSHIP_ASSET }} {{ memberProfileConstants.ASTERISK }}
            </v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-combobox
                v-model="record.relationshipsToAsset"
                :attach="true"
                :disabled="isEditable()"
                :items="relationshipToAssetOptions"
                :rules="assetSummaryConstants.relationshipToAssetRules"
                class="border-none checkbox-disabled"
                data-qe-id="relationshipToAssetDropdown"
                multiple
                required
                @change="onAssetRelationChange(record.relationshipsToAsset, relationshipToAssetOptions)">
            </v-combobox>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.ESTABLISHMENT }} {{ memberProfileConstants.ASTERISK }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-autocomplete
                v-model="record.establishmentType"
                :attach="true"
                :disabled="isEditable()"
                :items="assetSummaryConstants.establishmentTypes"
                :rules="assetSummaryConstants.establishmentRule"
                class="border-none"
                data-qe-id="establishmentTypeDropdown"
                required
                @change="onEstablishmentChange">
            </v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout v-show="record.establishmentType==assetSummaryConstants.ACCOMMODATION" class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.ACCOMODATION }} {{ memberProfileConstants.ASTERISK }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-autocomplete
                v-model="record.accommodationType"
                :attach="true"
                :disabled="isEditable()"
                :items="assetSummaryConstants.accommodationTypes"
                :rules="record.establishmentType==assetSummaryConstants.ACCOMMODATION ? assetSummaryConstants.accommodationRule: []"
                class="border-none"
                data-qe-id="accommodationTypeDropdown"
                required>
            </v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout v-show="record.establishmentType==assetSummaryConstants.DINING" class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.CUISINE }} {{ memberProfileConstants.ASTERISK }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-autocomplete
                v-model="record.cuisineType"
                :attach="true"
                :disabled="isEditable()"
                :items="assetSummaryConstants.cuisineTypes"
                :rules="record.establishmentType== assetSummaryConstants.DINING ? assetSummaryConstants.cuisineRule: []"
                class="border-none"
                data-qe-id="cuisineTypeDropdown"
                required>
            </v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout v-show="record.establishmentType==assetSummaryConstants.DINING" class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.DESTINATION_RESTAURANT }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-autocomplete
                v-model="record.destinationRestaurant"
                :attach="true"
                :disabled="isEditable()"
                :items="assetSummaryConstants.destinationRestaurant"
                class="border-none"
                data-qe-id="destinationRestaurantDropdown"
                item-text="name"
                item-value="value">
            </v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout v-show="record.establishmentType==assetSummaryConstants.DINING" class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.OTHER_CATEGORY }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field v-model="record.otherCategory" :disabled="isEditable()"
                          data-qe-id="otherCategoryTxt"></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.WEBSITE }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field
                v-if="!isEditable()"
                v-model="record.websiteUrl"
                :disabled="isEditable()"
                :rules="assetSummaryConstants.websiteUrlRule"
                data-qe-id="websiteUrlTxt">
            </v-text-field>
            <v-tooltip v-if="isEditable()" slot="append" bottom>
              <v-text-field
                  slot="activator"
                  v-model="record.websiteUrl"
                  :disabled="isEditable()"
                  :rules="assetSummaryConstants.websiteUrlRule"
                  data-qe-id="websiteUrlTxt">
              </v-text-field>
              <span>{{ record.websiteUrl }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.DISCOUNT_CODE }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field
                v-model="record.discountCode"
                :disabled="isEditable()"
                data-qe-id="discountCodeTxt">
            </v-text-field>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <span class="select-label">{{ assetSummaryConstants.TELEPHONE }}</span>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field
                v-if="isEditable() && !record.telephone"
                :disabled="isEditable()">
            </v-text-field>
            <ypo-phone-number
                v-if="telephone.show && (!isEditable() || record.telephone)"
                :can-clear="true"
                :is-disabled="isEditable()"
                :phone="telephone.number"
                :show-example="true"
                dataqeid="assetSummary_telephoneTxt"
                @update="handleTelephoneUpdate"
            />
            <v-flex v-if="!telephone.isValid" class="error-color" tag="small">
              {{ assetSummaryConstants.TELEPHONE_ERROR }}
            </v-flex>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.ADDITIONAL_INFORMATION }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field v-model="record.pointOfContact" :disabled="isEditable()"
                          data-qe-id="managerAdditionalInformation"></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <span class="select-label">{{ assetSummaryConstants.MANAGER_TELEPHONE }}</span>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field
                v-if="!record.pointOfContactTelephone && isEditable()"
                :disabled="isEditable()">
            </v-text-field>
            <ypo-phone-number
                v-if="managerTelephone.show && (!isEditable() || record.pointOfContactTelephone)"
                :can-clear="true"
                :is-disabled="isEditable()"
                :phone="managerTelephone.number"
                :show-example="true"
                dataqeid="assetSummary_managerTelephoneTxt"
                @update="handleManagerTelephoneUpdate"
            />
            <v-flex v-if="!managerTelephone.isValid" class="error-color" tag="small">
              {{ assetSummaryConstants.TELEPHONE_ERROR }}
            </v-flex>
          </v-flex>
        </v-layout>
        <v-layout class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.MANAGER_EMAIL }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-text-field
                v-model="record.pointOfContactEmail"
                :disabled="isEditable()"
                :rules="assetSummaryConstants.emailRule"
                data-qe-id="managerEmailTxt">
            </v-text-field>
          </v-flex>
        </v-layout>
        <v-layout v-show="record.establishmentType==assetSummaryConstants.DINING" class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.DOLLAR }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-autocomplete
                v-model="record.priceRange"
                :attach="true"
                :disabled="isEditable()"
                :items="assetSummaryConstants.priceRange"
                class="border-none"
                data-qe-id="priceRangeDropdown">
            </v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout v-show="record.establishmentType==assetSummaryConstants.ACCOMMODATION" class="form-control" row wrap>
          <v-flex sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.HOTEL_CLASS }}</v-flex>
          </v-flex>
          <v-flex sm8 xs12>
            <v-autocomplete
                v-model="record.hotelClassType"
                :attach="true"
                :disabled="isEditable()"
                :items="assetSummaryConstants.hotelClass"
                class="border-none"
                data-qe-id="hotelClassTypeDropdown">
            </v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout v-if="record.establishmentType==assetSummaryConstants.ACCOMMODATION" class="form-control" row wrap>
          <v-flex mb-3 sm4 xs12>
            <v-flex tag="span">{{ assetSummaryConstants.STAR_RATING }}</v-flex>
          </v-flex>
          <v-flex mb-3 sm8 xs12>
            <v-rating
                v-model="record.michelinStarRating"
                :hover="true"
                :readonly="isEditable()"
                data-qe-id="star-rating">
            </v-rating>
          </v-flex>
        </v-layout>
        <v-flex v-if="!isSaveEnable" class="owner_text">{{ assetSummaryConstants.OPERATOR_INFO }}</v-flex>
        <status-bar :action="saveAssetDetailsAction"></status-bar>
        <v-progress-linear v-if="getAddressProgress" indeterminate></v-progress-linear>
        <v-layout v-if="!viewFromNetwork" row wrap>
          <v-flex class="asset_owner" lg4 md6>
            <v-flex v-if="personAssetRelation.length > 0" class="add-owner__body inner-scrollbar add-owner-result">
              <v-flex v-for="(addedinvitee,i) in personAssetRelation" :key="i" class="add-owner__body__list-item">
                <v-avatar class="add-owner__body__list-item__avatar">
                  <img :alt="addedinvitee.name"
                       :onerror="imageErrorFallback(addedinvitee.fallbackProfileImageUrl)"
                       :src="addedinvitee.profileImageUrl"
                       class="dialog-box__img-format">
                </v-avatar>
                <v-flex class="add-owner__body__list-item__name" tag="strong">{{ addedinvitee.name }}</v-flex>
                <small>{{ addedinvitee.relationshipToAsset }}</small>
                <v-btn
                    v-if="!isEditable()"
                    class="edit_btn"
                    data-qe-id="ownerOperatorEdit"
                    icon
                    @click="showOwnerDialog(addedinvitee)">
                  <i class="fas fa-pencil-alt"></i>
                </v-btn>
                <v-btn
                    v-if="!isEditable()"
                    data-qe-id="ownerOperatorRemove"
                    icon
                    @click="deleteOwner(addedinvitee)">
                  <v-icon>close</v-icon>
                </v-btn>
              </v-flex>
            </v-flex>
            <v-btn
                :disabled="!isSaveEnable || isEditable()"
                class="dashed-square-btn owner-operator"
                data-qe-id="ownerOperatorBtn"
                @click="showOwnerDialog()">
              <i class="fas fa-plus-circle mr-2"></i> {{ assetSummaryConstants.OWNER_OPERATOR }}
            </v-btn>
            <AssetOwnerDialog
                v-if="isOwnerDialogOpen"
                v-model="isOwnerDialogOpen"
                :assetData="record"
                :selectedOwner="selectedOwner"
                @personAssetRelation="addPersonAssetRelation"
                @showAssetMappingExist="showAssetMappingExistPopup">
            </AssetOwnerDialog>
          </v-flex>
          <div id="results"></div>
          <v-flex class="asset-edit__action_items" sm12 text-lg-right text-xs-right xs12>
            <v-btn
                class="primary_btn transparent"
                data-qe-id="assetCancelBtn"
                flat
                @click="collapseForm()">
              {{ appCommonConstants.CANCEL }}
            </v-btn>
            <v-btn
                :disabled="!isSaveEnable || isEditable()"
                class="primary_btn"
                data-qe-id="assetSaveBtn"
                @click="saveAsset()">
              {{ appCommonConstants.SAVE }}
            </v-btn>
            <v-btn
                :disabled="!(valid && telephone.isValid && managerTelephone.isValid) || isEditable()"
                class="primary_btn save-submit"
                data-qe-id="assetSubmitBtn"
                @click="saveAndSubmit()">
              {{ assetSummaryConstants.SAVE_SUBMIT }}
            </v-btn>
          </v-flex>
        </v-layout>
        <v-layout v-if="viewFromNetwork" row wrap>
          <v-flex class="asset_owner" xs12>
            <v-flex v-if="personAssetRelation.length > 0" class="add-owner__body inner-scrollbar add-owner-result">
              <v-flex v-for="(user,i) in personAssetRelation" :key="i" class="add-owner__body__list-item">
                <v-avatar class="add-owner__body__list-item__avatar">
                  <img :alt="user.name"
                       :onerror="imageErrorFallback(user.fallbackProfileImageUrl)"
                       :src="user.profileImageUrl"
                       class="dialog-box__img-format">
                </v-avatar>
                <v-flex class="add-owner__body__list-item__name" tag="strong">{{ user.name }}
                </v-flex>
                <small>{{ user.relationshipToAsset }}&nbsp;
                  <v-flex v-if="user.isSubmitted"
                          class="add-owner__body__list-item__label-box"
                          tag="strong">{{ assetSummaryConstants.SUBMITTED_BY }}
                  </v-flex>
                </small>
              </v-flex>
            </v-flex>
          </v-flex>
          <v-flex text-xs-right xs12>
            <status-bar :action="approveAssetRequestAction"></status-bar>
            <v-btn class="primary_btn transparent" data-qe-id="assetDenyBtn" flat @click="confirmDeny()">
              {{ assetSummaryConstants.DENY }}
            </v-btn>
            <v-btn class="primary_btn save-submit"
                   data-qe-id="assetApproveBtn"
                   @click="acceptAssetRequest()">
              {{ assetSummaryConstants.APPROVE }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-form>
      <v-dialog v-model="deleteAssetOwnerDialog" absolute content-class="post-actions" lazy persistent>
        <v-card>
          <v-flex tag="h6"> {{ assetSummaryConstants.ASSET_OWNER_DELETE_TITLE }}
            <v-btn data-qe-id="dialogCloseIcon" icon @click="deleteAssetOwnerDialog=false">
              <v-icon size="20">close</v-icon>
            </v-btn>
          </v-flex>
          <v-flex tag="p">
            {{ assetSummaryConstants.ASSET_OWNER_DELETE_TEXT }}
          </v-flex>
          <v-flex class="text-xs-right">
            <status-bar :action="deletAssetOwnerAction"></status-bar>
            <v-btn class="primary_btn transparent" data-qe-id="dialogCloseBtn"
                   flat
                   @click="deleteAssetOwnerDialog=false">
              {{ appCommonConstants.CANCEL }}
            </v-btn>
            <v-btn class="primary_btn" dark data-qe-id="dialogConfirmBtn" @click="deleteAssetOwner">
              {{ appCommonConstants.DELETE }}
            </v-btn>
          </v-flex>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showAssetExistDialog" absolute content-class="post-actions" lazy persistent>
        <v-card>
          <v-flex tag="h6"> {{ assetSummaryConstants.ASSET_ALREADY_EXIST_TITLE }}
            <v-btn data-qe-id="dialogCloseIcon" icon @click="showAssetExistDialog=false">
              <v-icon size="20">close</v-icon>
            </v-btn>
          </v-flex>
          <v-flex tag="p">
            {{ assetSummaryConstants.ASSET_ALREADY_EXIST_TEXT }}
          </v-flex>
          <v-flex class="text-xs-right">
            <v-btn class="primary_btn transparent" data-qe-id="dialogCloseBtn"
                   flat
                   @click="showAssetExistDialog=false">
              {{ assetSummaryConstants.NO }}
            </v-btn>
            <v-btn class="primary_btn" dark data-qe-id="dialogConfirmBtn_map" @click="mapAssetAlreadyExist">
              {{ assetSummaryConstants.YES }}
            </v-btn>
          </v-flex>
        </v-card>
      </v-dialog>
      <v-dialog
          v-model="denialConfiramationDialog"
          absolute
          content-class="small"
          lazy>
        <v-card class="custom-dailog">
          <v-card-title>
            {{ assetSummaryConstants.DENIED_REASON_TITLE }}
            <v-icon data-qe-id="confirmationCancelIcon" @click="denialConfiramationDialog=false">fas fa-times</v-icon>
          </v-card-title>
          <v-card-text>
            <v-flex class="reject_reason__cont">
              <v-textarea v-model="deniedReason"
                          :placeholder="assetSummaryConstants.DENIED_CONFIRMATION_TITLE"
                          autofocus
                          counter="100"
                          filled
                          maxlength="100">
              </v-textarea>
            </v-flex>
          </v-card-text>
          <v-card-actions>
            <v-flex pa-2 pr-3 text-xs-right xs12>
              <status-bar :action="denyAssetRequestAction"></status-bar>
              <v-btn class="primary_btn transparent" data-qe-id="confirmationCancelBtn"
                     @click="denialConfiramationDialog = false">
                {{ appCommonConstants.CANCEL }}
              </v-btn>
              <v-btn :disabled="deniedReason==''"
                     class="primary_btn"
                     data-qe-id="confirmationAndDenyBtn"
                     @click="submitAssetDeny()">
                {{ assetSummaryConstants.CONFIRM_AND_DENY }}
              </v-btn>
            </v-flex>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
          v-model="openSuggestedAddressDialog"
          absolute
          content-class="medium"
          lazy
          persistent
          scrollable>
        <v-card class="custom-dailog">
          <v-card-title class="google-address-title">
            {{ assetSummaryConstants.ASSET_CONFIRM_ADDRESS }}
            <i class="fas fa-times"
               data-qe-id="dialog_close_btn"
               @click="openSuggestedAddressDialog=false"></i>
          </v-card-title>
          <v-card-text class="inner-scrollbar">
            <v-radio-group v-model="selectedAddress">
              <v-radio v-for="(result, i) in results" :key="i"
                       :label="result.formatted_address" :value="result" class="radio-google-address"
                       data-qe-id="select_suggested_address">
              </v-radio>
            </v-radio-group>
          </v-card-text>
          <v-card-actions>
            <v-flex class="pa-3" text-xs-right>
              <v-progress-linear v-if="setAddressProgress" indeterminate></v-progress-linear>
              <v-btn class="primary_btn transparent small" data-qe-id="dialog_cancel_btn"
                     @click="openSuggestedAddressDialog=false">
                {{ appCommonConstants.CANCEL }}
              </v-btn>
              <v-btn class="primary_btn small" data-qe-id="dialog_confirm_btn" @click="setNewAddress()">
                {{ appCommonConstants.CONFIRM }}
              </v-btn>
            </v-flex>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-flex>
</template>

<script>
import { memberProfileConstants, assetSummaryConstants, onAssetRelationChange } from "../memberProfile.constants";
import { appCommonConstants } from "../../appCommon.constants.js";
import { mapGetters, mapActions, mapState, mapMutations } from "vuex";
import { storeAction } from "../../../store/storeActions.constant";
import { storeGetters } from "../../../store/storeGetters.constant";
import { imageErrorFallback, zipPostalCodeValidation, onlyNumber } from "../../../common/utils/utils";
import AssetOwnerDialog from './AssetOwnerDialog';
import StatusBar from "../../StatusBar/StatusBar";
import Vue from 'vue';
import ConfirmationDialog from '../EditDialogues/ConfirmationDialog';
import GoogleSearch from "../../Google/GoogleSearch";
import { gmapApi } from 'vue2-google-maps';
import { storeMutation } from '../../../store/storeMutations.constant';
import { isNullOrUndefined } from 'util';
import YpoPhoneNumber from "@/components/YpoPhoneNumber/YpoPhoneNumber.vue";

export default {
  name: 'AssetSummaryEdit',
  components: {
    YpoPhoneNumber,
    AssetOwnerDialog,
    ConfirmationDialog,
    StatusBar,
    GoogleSearch,
  },
  data: () => ({
    memberProfileConstants,
    assetSummaryConstants,
    appCommonConstants,
    items: [],
    record: [],
    countries: [],
    valid: false,
    deleteAssetOwnerDialog: false,
    isOwnerDialogOpen: false,
    timeoutForTypeAhead: null,
    isFourFieldsFilled: false,
    showAssetExistDialog: false,
    personAssetRelation: [],
    selectedOwner: null,
    states: [],
    isSaveAndSubmit: false,
    results: [],
    openSuggestedAddressDialog: false,
    selectedAddress: null,
    getAddressProgress: false,
    isFieldChange: false,
    setAddressProgress: false,
    denialConfiramationDialog: false,
    deniedReason: '',
    relationshipToAssetOptions: [],
    lat: null,
    lng: null,
    onlyNumber,
    telephone: {
      isValid: true,
      code: '',
      number: '',
      show: true,
    },
    managerTelephone: {
      isValid: true,
      code: '',
      number: '',
      show: true,
    },
  }),
  props: {
    formData: {
      type: Object,
      required: true,
    },
    memberId: {
      type: String,
      required: true,
    },
    viewFromNetwork: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    this.getCountryAndRegions();
    this.record = Vue.util.extend({}, this.formData.item);
    this.relationshipToAssetOptions = Vue.util.extend([], assetSummaryConstants.relationshipToAsset);

    if (this.record.country) {
      this.loadStates(this.record.country);
    }

    if (this.record.personAssetRelation) {
      if (this.viewFromNetwork) {
        const personsMapData = Vue.util.extend([], this.record.personAssetRelation.slice());
        const submittedUser = personsMapData.find(x => x.personId === this.record.lastUpdatedBy);
        if (submittedUser) {
          submittedUser.isSubmitted = true;
          this.personAssetRelation.push(submittedUser);
        }
        this.personAssetRelation.push(...personsMapData.filter(x => x.personId !== this.record.lastUpdatedBy));
      } else {
        const personassets = Vue.util.extend([], this.record.personAssetRelation);
        personassets.forEach((x) => {
          if (x.personId === this.memberId) {
            if (x.relationshipToAsset) {
              this.record.relationshipsToAsset = x.relationshipToAsset.split(',').map(j => j.trim());
              this.onAssetRelationChange(this.record.relationshipsToAsset, this.relationshipToAssetOptions);
            } else {
              this.record.relationshipsToAsset = [];
              this.relationshipToAssetOptions.forEach((item) => {
                item.disabled = false;
              });
            }
          } else {
            x.relationshipsToAsset = x.relationshipToAsset ? x.relationshipToAsset.split(',').map(j => j.trim()) : [];
            this.personAssetRelation.push(x);
          }
        });
      }
    } else {
      this.onAssetRelationChange([], this.relationshipToAssetOptions);
    }
  },
  mounted() {
    if (this.record.telephone) {
      this.telephone.number = this.record.telephone;
    }
    if (this.record.pointOfContactTelephone) {
      this.managerTelephone.number = this.record.pointOfContactTelephone;
    }
    if (this.record.telephoneCountryCode) {
      this.$refs.telephone.setCountry(this.record.telephoneCountryCode, this.record.country);
    }
    if (this.record.pointOfContactTelephoneCountryCode) {
      this.$refs.managerTelephone.setCountry(this.record.pointOfContactTelephoneCountryCode, this.record.country);
    }
  },
  methods: {
    ...mapActions({
      getCountryAndRegions: `memberProfile/${storeAction.getCountryAndRegions}`,
      updateSubmitAssetDetails: `memberProfile/${storeAction.updateSubmitAssetDetails}`,
      saveSubmitAssetDetails: `memberProfile/${storeAction.saveSubmitAssetDetails}`,
      deleteAssets: `memberProfile/${storeAction.deleteAssets}`,
      getStateOrProvinces: `memberProfile/${storeAction.getStateOrProvinces}`,
      denyAssetRequest: `memberProfile/${storeAction.denyAssetRequest}`,
      approveAssetRequest: `memberProfile/${storeAction.approveAssetRequest}`,
    }),
    ...mapMutations({
      setStreetAddress: `memberProfile/${storeMutation.setStreetAddress}`,
    }),
    imageErrorFallback,
    onAssetRelationChange,
    zipPostalCodeValidation,
    fillRecordData(value) {
      this.record = {
        ...this.record,
        name: value.name || '',
        city: value.city || '',
        country: value.country || '',
        zip: value.zip || '',
        websiteUrl: value.websiteUrl || '',
        telephone: value.telephone || '',
        address1: value.address1 || this.streetAddress,
        address2: value.address2 || '',
        state: value.state || '',
        longitude: value.longitude || '',
        latitude: value.latitude || '',
        telephoneCountryCode: value.telephoneCountryCode || '',
      };

      if (Object.keys(value).length === 0) {
        this.setStreetAddress('');
        this.record.address1 = this.streetAddress;
      }

      this.reloadTelephone();
      this.reloadManagerTelephone();
      this.record.pointOfContactTelephone = null;
      this.record.pointOfContactTelephoneCountryCode = null;
      this.managerTelephone.number = "";
      this.telephone.number = value.telephone;
    },
    collapseForm() {
      this.$emit('close');
    },
    onRegionsChange(val) {
      delete this.record.country;
      delete this.record.state;
      this.states = [];
      this.isFieldChange = true;
      this.countries = this.countryAndRegions[val].sort();
    },
    onCountryChange(val) {
      if (val) {
        this.loadStates(val);
        delete this.record.state;
        Object.keys(this.countryAndRegions).forEach((x) => {
          if (this.countryAndRegions[x].includes(val)) {
            this.record.region = x;
          }
        });
        this.isFieldChange = true;
        this.reCreateTelephones();
      }
    },
    reCreateTelephones() {
      if (!this.record.pointOfContactTelephone) {
        this.reloadManagerTelephone();
      }
      if (!this.record.telephone) {
        this.reloadTelephone();
      }
    },
    reloadManagerTelephone() {
      this.managerTelephone.show = false;
      setTimeout(() => {
        this.managerTelephone.show = true;
      }, 10);
    },
    reloadTelephone() {
      this.telephone.show = false;
      setTimeout(() => {
        this.telephone.show = true;
      }, 10);
    },
    showOwnerDialog(owner) {
      this.isOwnerDialogOpen = !this.isOwnerDialogOpen;
      if (owner) {
        this.selectedOwner = owner;
      } else {
        this.selectedOwner = null;
      }
    },
    saveAndSubmit() {
      const ref = this.$refs.assetSummaryForm;
      this.isSaveAndSubmit = true;
      this.record.isSaveAndSubmit = true;
      if (ref && ref.validate && ref.validate()) {
        if (!this.$refs.googleSearch.isSearch || this.isFieldChange) {
          this.getSuggestedAddress();
        } else {
          this.addPersonAssetMappings();
          if (!this.record.id) {
            this.saveSubmitAssetDetails([this.record]).then((res) => {
              this.afterSaveOrSubmit(res, true);
            });
          } else {
            this.updateSubmitAssetDetails(this.record).then((res) => {
              this.afterSaveOrSubmit(res, true);
            });
          }
        }
      } else {
        this.valid = false;
      }
    },
    isEditable() {
      return this.record.assetStatus === "Review Pending";
    },
    isDenied() {
      return this.record.assetStatus === "Denied";
    },
    checkFourFieldFilled(data) {
      if (!data.name || !data.address1 || !data.city || !data.country) {
        return false;
      }
      return true;
    },
    displaySuggestions(predictions, status) {
      this.results = [];
      this.getAddressProgress = false;
      if (google && status !== google.maps.places.PlacesServiceStatus.OK) {
        this.saveEnteredAddress();
        return;
      }
      predictions.slice(0, 5).forEach((x) => {
        this.results.push({ formatted_address: x.formatted_address, place_id: x.place_id });
      });
      this.results.push({ formatted_address: this.assetSummaryConstants.NONE_OF_ABOVE });
      this.selectedAddress = this.results.length > 0 ? this.results[0] : null;
      this.openSuggestedAddressDialog = true;
    },
    saveEnteredAddress() {
      this.$refs.googleSearch.isSearch = true;
      this.isFieldChange = false;
      this.record.latitude = this.lat;
      this.record.longitude = this.lng;
      if (this.isSaveAndSubmit) {
        this.saveAndSubmit();
      } else {
        this.saveAsset();
      }
    },
    setNewAddress() {
      const addressId = this.selectedAddress.place_id;
      if (isNullOrUndefined(addressId)) {
        this.openSuggestedAddressDialog = false;
        this.saveEnteredAddress();
        return;
      }
      this.setAddressProgress = true;
      const request = {
        placeId: addressId,
        fields: this.assetSummaryConstants.FIELDS,
      };
      const service = new google.maps.places.PlacesService(document.getElementById('results'));
      service.getDetails(request, (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK) {
          console.log(status);
          return;
        }
        this.openSuggestedAddressDialog = false;
        this.$refs.googleSearch.place_changed(place);
        this.setAddressProgress = false;
        this.isFieldChange = false;
        this.$refs.googleSearch.isSearch = true;
      });
    },
    saveAsset() {
      if (!this.$refs.googleSearch.isSearch || this.isFieldChange) {
        this.getSuggestedAddress();
      } else {
        this.addPersonAssetMappings();
        if (!this.record.id) {
          this.saveSubmitAssetDetails([this.record]).then((res) => {
            this.afterSaveOrSubmit(res, false);
          });
        } else {
          this.updateSubmitAssetDetails(this.record).then((res) => {
            this.afterSaveOrSubmit(res, false);
          });
        }
      }
    },
    getSuggestedAddress() {
      this.setStreetAddress(this.record.address1);
      this.getAddressProgress = true;
      const input = [];
      input.push(this.record.address1, this.record.city, this.record.state, this.record.country);
      const arr = input.join(", ");
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ 'address': arr }, ((results, status) => {
        if (status !== google.maps.GeocoderStatus.OK) {
          console.log(status);
          return;
        }
        this.lat = results[0].geometry.location.lat();
        this.lng = results[0].geometry.location.lng();
        const addressLocation = new google.maps.LatLng(this.lat, this.lng);

        const request = {
          location: addressLocation,
          query: this.record.name,
        };
        const service = new google.maps.places.PlacesService(document.getElementById('results'));
        service.textSearch(request, this.displaySuggestions);
      }));
    },
    mapAssetAlreadyExist() {
      this.record.isAssociationRequired = true;
      if (this.isSaveAndSubmit) {
        this.saveAndSubmit();
      } else {
        this.saveAsset();
      }
      this.showAssetExistDialog = false;
    },
    addPersonAssetRelation(value) {
      const assetMapping = this.personAssetRelation.find(x => x.personId === value.personId);
      if (assetMapping) {
        assetMapping.isActive = true;
        assetMapping.relationshipToAsset = value.relationshipsToAsset.map(x => x.text || x).join(',');
        assetMapping.relationshipsToAsset = value.relationshipsToAsset.map(x => x.text || x);
      } else {
        value.isActive = true;
        value.relationshipToAsset = value.relationshipsToAsset.map(x => x.text || x).join(',');
        this.personAssetRelation.push(value);
      }
      this.selectedOwner = null;
    },
    addCurrentUserToAsset() {
      const relationship = this.record.relationshipsToAsset ? this.record.relationshipsToAsset.map(x => x.text || x).join(',') : "";
      if (!this.record.personAssetRelation) {
        this.record.personAssetRelation = [{
          personId: this.memberId,
          relationshipToAsset: relationship,
          isActive: true,
        }];
      } else {
        const currentUser = this.record.personAssetRelation.find(x => x.personId === this.memberId);
        if (currentUser) {
          currentUser.relationshipToAsset = relationship;
          currentUser.isActive = true;
        } else {
          this.record.personAssetRelation.push({
            personId: this.memberId,
            relationshipToAsset: relationship,
            isActive: true,
          });
        }
      }
    },
    afterSaveOrSubmit(res, isSubmit) {
      const resp = res.data || res.response.data;
      this.isSaveAndSubmit = isSubmit;
      if (resp && resp.message === this.assetSummaryConstants.ASSET_ALREADY_EXIST_RESP) {
        this.showAssetExistDialog = true;
      } else if (isSubmit) {
        this.$emit('alert', 'submissionCompleteDialog');
      } else {
        this.$emit('alert', 'saveSuccessDialog');
      }
    },
    onEstablishmentChange() {
      delete this.record.accommodationType;
      delete this.record.cuisineType;
      delete this.record.destinationRestaurant;
      delete this.record.otherCategory;
      delete this.record.hotelClassType;
      delete this.record.michelinStarRating;
      delete this.record.priceRange;
    },
    deleteOwner(owner) {
      this.deleteAssetOwnerDialog = true;
      this.selectedOwner = owner;
    },
    deleteAssetOwner() {
      const me = this;
      const index = me.personAssetRelation.findIndex(x => x === me.selectedOwner);
      me.personAssetRelation.splice(index, 1);

      const deletedOwner = me.record.personAssetRelation.find(x => x === me.selectedOwner);
      if (deletedOwner) {
        deletedOwner.isActive = false;
      }

      me.selectedOwner = null;
      me.deleteAssetOwnerDialog = false;
    },
    showAssetMappingExistPopup() {
      this.$emit('alert', 'showAssetMappingExistDialog');
    },
    loadStates(country) {
      this.getStateOrProvinces(country).then((res) => {
        if (res && res.data.length > 0) {
          this.states = res.data.filter(x => x.stateProvince != null);
        }
      });
    },
    addPersonAssetMappings() {
      const me = this;
      me.addCurrentUserToAsset();
      me.personAssetRelation.forEach((data) => {
        const ownerData = me.record.personAssetRelation.find(x => x.personId === data.personId);
        if (ownerData) {
          ownerData.isActive = true;
        } else {
          data.relationshipToAsset = data.relationshipsToAsset.map(x => x.text || x).join(',');
          me.record.personAssetRelation.push(data);
        }
      });
    },
    confirmDeny() {
      this.denialConfiramationDialog = true;
      this.deniedReason = '';
    },
    submitAssetDeny() {
      const body = {
        id: this.record.id,
        deniedReason: this.deniedReason,
      };
      this.denyAssetRequest(body).then((res) => {
        this.denialConfiramationDialog = false;
        if (res) {
          this.$emit('alert', 'showAssetDenySuccessDialog');
        }
      });
    },
    acceptAssetRequest() {
      const body = {
        id: this.record.id,
      };
      this.approveAssetRequest(body).then((res) => {
        if (res) {
          this.$emit('alert', 'showAssetApproveSuccessDialog');
        }
      });
    },
    handleTelephoneUpdate(data) {
      this.telephone.isValid = !data.isInvalid;
      if (data.phone) {
        this.record.telephone = data.phone;
        this.record.telephoneCountryCode = data.countryCode;
      } else {
        this.record.telephone = null;
        this.record.telephoneCountryCode = null;
      }
    },
    handleManagerTelephoneUpdate(data) {
      this.managerTelephone.isValid = !data.isInvalid;
      if (data.phone) {
        this.record.pointOfContactTelephone = data.phone;
        this.record.pointOfContactTelephoneCountryCode = data.countryCode;
      } else {
        this.record.pointOfContactTelephone = null;
        this.record.pointOfContactTelephoneCountryCode = null;
      }
    },
  },
  computed: {
    google: gmapApi,
    ...mapGetters({
      countryAndRegions: `memberProfile/${storeGetters.countryAndRegions}`,
      streetAddress: `memberProfile/${storeGetters.streetAddress}`,
    }),
    ...mapState("actionState", ["actions"]),
    saveAssetDetailsAction() {
      return this.actions[storeAction.saveAssetDetails];
    },
    isSaveEnable() {
      return this.checkFourFieldFilled(this.record);
    },
    deletAssetOwnerAction() {
      return this.actions[storeAction.deleteAssets];
    },
    denyAssetRequestAction() {
      return this.actions[storeAction.denyAssetRequest];
    },
    approveAssetRequestAction() {
      return this.actions[storeAction.approveAssetRequest];
    },
  },
  watch: {
    record(val) {
      if (val.country) {
        this.loadStates(val.country);
        Object.keys(this.countryAndRegions).forEach((x) => {
          if (this.countryAndRegions[x].includes(val.country)) {
            this.record.region = x;
          }
        });
      }
    },
    countryAndRegions(val) {
      if (val) {
        Object.values(val).forEach((x) => {
          this.countries.push(...x);
          this.countries = this.countries.sort();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../common/styles/_colors.scss";
@import "../../../common/styles/_responsive.scss";

.asset-edit {
  background-color: rgba($ypo-blue-grey, 0.3);
  border-radius: 4px;
  box-shadow: 0 1px 3px 0 rgba($black, 0.2);
  border: 2px solid rgba($ypo-blue-grey, 0.8);
  transition: all 0.5s;
  margin: 0px 90px 30px 90px;

  .owner_text {
    color: $ypo_cta;
    margin: 5px;
  }

  .alert__box {
    &__warning {
      background: $ypo-gold;
      color: $white;
      position: relative;
      padding: 10px 0 10px 70px;
      font-size: 12px;
      text-transform: initial;
      line-height: normal;
      margin-left: -3px;
      margin-bottom: 10px;
      font-weight: 600;

      i {
        font-size: 20px !important;
        color: $white !important;
        position: absolute;
        left: 40px;
        top: 7px;
        bottom: 0;
      }
    }

    &__error {
      background: $ypo-red;
      color: $white;
      position: relative;
      padding: 10px 0 10px 70px;
      font-size: 12px;
      text-transform: initial;
      line-height: normal;
      margin-left: -3px;
      margin-bottom: 10px;
      font-weight: 600;

      i {
        font-size: 20px !important;
        color: $white !important;
        position: absolute;
        left: 40px;
        top: 9px;
        bottom: 0;
      }

      span {
        font-weight: 500;
      }
    }
  }

  .form-control {
    span {
      color: $ypo-cta;
      display: block;
    }

    small {
      color: $ypo-cta;
      font-size: 12px;
    }

    .error-color {
      color: $ypo-error-red;
    }

    .vue-tel-input {
      box-shadow: none !important;
      border: 0;
      border-bottom: 1px solid rgba($black, 0.5);
      border-radius: 0;

      .dropdown {
        padding: 0 7px !important;
      }
    }
  }

  .dashed-square-btn {
    padding: 0.5em 1em;
    text-decoration: none;
    color: $ypo-cta;
    border: dashed 1px $ypo-cta;
    border-radius: 3px;
    transition: .4s;
    font-weight: bold;

    &:hover {
      border-style: dotted;
      color: $ypo-cta-hover;
    }
  }

  .asset_owner {
    position: relative;
  }

  .add-owner {
    border: 0 !important;
    z-index: 1;
    position: absolute;
    left: 40px;
    top: 35px;
    width: 100%;

    h3 {
      color: $white;
      background: $ypo_blue;
      font-size: 14px;
      padding: 10px 15px;
      letter-spacing: 0;
      font-weight: 500;
      position: relative;

      i {
        position: absolute;
        right: 10px;
        cursor: pointer;
      }
    }

    &__title {
      padding: 10px 5px 5px 15px;
      border-bottom: 1px solid $ypo-medium-gray;
      margin-bottom: 10px;
      font-size: 14px;
      text-transform: uppercase;
      color: rgba($ypo_blue, 0.61);
      align-items: center;
      font-weight: 600;

      .v-btn {
        margin: 0 -5px 0 0px;
      }
    }

    &__body {
      max-height: 350px;
      padding: 15px;
      overflow-y: auto;

      &__list-item {
        position: relative;
        padding: 10px 90px 10px 55px;
        border-bottom: 1px $ypo-medium-gray solid;
        align-items: center;
        min-height: 60px;
        display: inline-block;
        width: 100%;

        &:hover {
          background: $ypo-open-blue-gray;

          .v-btn {
            opacity: 1;
            visibility: visible;
          }
        }

        &__avatar {
          position: absolute;
          left: -1px;
          top: 5px;

          img {
            height: 40px;
            width: 40px;
          }
        }

        &__name {
          font-size: 12px;
          color: $ypo-dark-background-gray;
          font-weight: 600;
          display: block;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }

        &__label-box {
          color: $white;
          display: inline-block;
          background-color: $ypo-blue;
          border-radius: 4px;
          padding-inline-start: 2px;
          padding-inline-end: 2px;
        }

        small {
          display: block;
          color: $ypo-dark-background-gray;
        }

        .v-btn {
          position: absolute;
          right: 0px;
          z-index: 2;
          top: 5px;
          opacity: 0;
          visibility: hidden;
          transition: 0.5s all ease;

          &.edit_btn {
            right: 40px;
          }

          .v-icon {
            color: $ypo_blue !important;
            font-size: 16px;
          }
        }
      }

      &.add-owner-result {
        padding-top: 0;
      }
    }
  }

  @include breakpoint(xs) {
    max-width: 343px;
    margin: 0;

    &__responsive {
      padding: 15px !important;

      .owner-operator {
        width: 100%;
        margin: 10px 0 0 0;
      }

      .save-submit {
        margin-top: 20px;
      }

      .add-owner {
        left: 0;
        top: 50px;
      }
    }

  }
  @include breakpoint(md) {
    &__action_items {
      margin-top: 20px;
    }
  }

  &__dialog {
    position: fixed;
    left: 0;
    top: 0px;
    width: 100%;
    height: 100%;
    z-index: 203;
  }
}

.google-address-title {
  font-size: 18px !important;
}
</style>
<style lang="scss">
@import "../../../common/styles/_colors.scss";

.asset-edit {
  .form-control {
    margin-bottom: 30px;
    align-items: center;

    .v-input {
      margin-top: 0;
      padding-top: 0;
    }

    .v-input__control {
      min-height: inherit;
    }

    .v-input__slot {
      background: transparent !important;
      box-shadow: none !important;
      padding-left: 0 !important;
      font-size: 14px;
      margin-bottom: 0;

      &:before {
        border-color: rgba($black, 0.2);
      }
    }

    .checkbox-disabled {
      font-weight: 600;

      .theme--light.v-icon {
        color: inherit !important;
      }
    }

    .vue-tel-input {
      .dropdown {
        padding: 7px 0;

        .iti-flag {
          margin-left: 1px;
        }

        .country-code {
          font-size: 14px;
          font-weight: 600;
        }
      }
    }
  }

  .v-label,
  input {
    font-size: 14px;
    font-weight: bold;
    color: $ypo-gray-text !important;

    &:disabled {
      -webkit-text-fill-color: $ypo-gray;
      -webkit-opacity: 1;
      color: $ypo-gray !important;
      opacity: 1;
    }

    &.link {

      .v-label,
      input {
        color: $ypo-cta !important;
      }
    }

    .v-menu__content {
      box-shadow: 0 2px 5px 0 rgba($black, 0.18);
      border: 1.5px solid rgba($ypo-text-border, 0.25);
      border-radius: 3px;

      .v-list {
        background: $ypo-extra-light-gray;

        & > div {
          &:hover {
            background: $ypo-cta;

            .v-list__tile__title {
              color: $white;
            }
          }

          .v-list__tile__title {
            font-size: 16px;
          }
        }
      }
    }

    &.autocomplete-textfield {
      border-bottom: 1px solid rgba($black, 0.3);
      width: 100%;

      &:focus {
        border-bottom: 2px solid rgba($black, 0.87);
        outline: none;
      }

      &.empty {
        border-bottom: 1px solid $ypo-error-red;
      }
    }
  }
}

.radio-google-address {
  label {
    font-weight: 500 !important;
  }
}
</style>
